// The Offer page's seam (lane B8, S12). OfferTabProps is the one interface S13's Compensation and Reference Check tabs
// take. The OfferOut fields S05/S07/S08 add (the display chain, negotiation rounds, the candidate-facing status, the
// decision, the ranked alternates and the published reason lists) are read through `seam()`, which gives them the
// shapes below.

import type { OfferOpenOutput } from "@/api/tools";

export interface OfferTabProps {
  requisitionId: string;
  applicationId: string;
  offer: OfferOpenOutput;
  reload: () => void;
}

/** One step of the display chain (D7): derived from the event log, never stored; days are calendar days. */
export interface ChainStep {
  key: string;
  state: "done" | "current" | "pending";
  at: string | null;
  days: number | null;
}

export interface CounterItem {
  field: string;
  value?: number | string | null;
  text?: string | null;
}

/** One negotiation round (D7). `candidate_note` is absent from the Coordinator's projection. */
export interface NegotiationRound {
  id?: string | null;
  round_no: number;
  requested: CounterItem[];
  source: string;
  received_at: string;
  candidate_note?: string | null;
}

export interface OfferDecision {
  kind: string;
  decided_at: string | null;
  reason_code: string | null;
  reason_label: string | null;
  next_path: NextPath | null;
}

/** An active alternate (D8): display_name only where the seat holds identity (POLICY §8.2). */
export interface OfferAlternate {
  cand: string;
  application_id?: string | null;
  display_name: string | null;
  rank: number;
  days_held: number;
}

/** A published reason (the Admin's configuration): its slug, its configured label, and whether it needs text. */
export interface ReasonOption {
  value: string;
  label: string;
  requires_note?: boolean;
}

export type NextPath = "alternate" | "continue" | "reopen" | "close";
export const NEXT_PATHS: readonly NextPath[] = ["alternate", "continue", "reopen", "close"];

export interface OfferSeam {
  chain?: { steps: ChainStep[]; current: string | null } | null;
  negotiation?: NegotiationRound[] | null;
  candidate_status?: { word: string; at: string | null } | null;
  decision?: OfferDecision | null;
  alternates?: OfferAlternate[] | null;
  // the published lists the dialogs need (reasons:offer_decline, reasons:withdrawal and reasons:cancel_category);
  // null while a list is unpublished, and the dialog stays blocked
  decline_reasons?: ReasonOption[] | null;
  withdrawal_reasons?: ReasonOption[] | null;
  reporting_categories?: ReasonOption[] | null;
}

/** The generated client types these keys loosely (the server declares them as plain objects); the seam's shapes win. */
export type OfferView = Omit<OfferOpenOutput, keyof OfferSeam> & OfferSeam;

/** The offer read as the seam S05/S07/S08 publish. */
export function seam(offer: OfferOpenOutput): OfferView {
  return offer as unknown as OfferView;
}

/** The capabilities the person holds (roles from the session; `admin` is the legacy Root Owner alias). */
export type Roles = readonly string[];
